import React from "react";
import { motion } from "framer-motion";

interface WordRevealProps {
  text: string;
  className?: string;
  highlightWords?: string[];
  highlightClass?: string;
}

export const WordReveal: React.FC<WordRevealProps> = ({
  text,
  className = "",
  highlightWords = [],
  highlightClass = "text-primary",
}) => {
  const words = text.split(" ");

  return (
    <motion.span
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.06 } },
      }}
    >
      {words.map((word, i) => {
        const isHighlight = highlightWords.includes(word.replace(/[.,!?]/g, ""));
        return (
          <React.Fragment key={i}>
            <motion.span
              className={`inline-block ${isHighlight ? highlightClass : ""}`}
              variants={{
                hidden: { opacity: 0, y: 12, filter: "blur(4px)" },
                visible: { opacity: 1, y: 0, filter: "blur(0px)" },
              }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {word}
            </motion.span>
            {i < words.length - 1 && " "}
          </React.Fragment>
        );
      })}
    </motion.span>
  );
};

export const LetterReveal: React.FC<{
  text: string;
  className?: string;
}> = ({ text, className = "" }) => (
  <motion.span
    className={`inline-block ${className}`}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={{
      hidden: {},
      visible: { transition: { staggerChildren: 0.03 } },
    }}
  >
    {text.split("").map((char, i) => (
      <motion.span
        key={i}
        className="inline-block"
        variants={{
          hidden: { opacity: 0, y: 8 },
          visible: { opacity: 1, y: 0 },
        }}
        transition={{ duration: 0.3 }}
      >
        {char === " " ? "\u00A0" : char}
      </motion.span>
    ))}
  </motion.span>
);

export const GradientTextScroll: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <motion.span
    className={`bg-gradient-to-r from-primary via-warm-orange to-primary bg-clip-text text-transparent bg-[length:200%_100%] ${className}`}
    animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
  >
    {children}
  </motion.span>
);
